import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';
import { PaletteMode } from '@mui/material';
import propertyAnalysisReducer from './store/propertyAnalysisSlice';
import tradingReducer from './store/tradingSlice';

// Types for conversations
export interface Conversation {
  _id: string;
  userId: string;
  title: string;
  status: 'active' | 'archived' | 'deleted';
  type: 'screenshare' | 'general';
  isPinned?: boolean;
  metadata?: {
    screenCaptureMode?: 'full' | 'window' | 'region';
    aiModel?: string;
    sessionDuration?: number;
    messageCount?: number;
    lastActivity?: Date;
    lastMessageAt?: Date;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface ConversationsState {
  items: Record<string, Conversation>;
  ids: string[];
  pinnedIds: string[];
  searchResults: string[];
  isLoading: boolean;
  error: string | null;
  activeConversationId: string | null;
  searchTerm: string;
  groupBy: 'date' | 'type' | 'tag' | 'pinned';
}

// Sidebar UI state
export interface SidebarState {
  isCollapsed: boolean;
  width: number;
  isVisible: boolean;
}

// Theme slice
const getInitialTheme = (): PaletteMode => {
  const stored = localStorage.getItem('theme');
  if (stored === 'light' || stored === 'dark') return stored;
  return 'dark';
};

const themeSlice = createSlice({
  name: 'theme',
  initialState: getInitialTheme() as PaletteMode,
  reducers: {
    setTheme: (state, action: PayloadAction<PaletteMode>) => {
      localStorage.setItem('theme', action.payload);
      return action.payload;
    },
    toggleTheme: (state) => {
      const newTheme: PaletteMode = state === 'dark' ? 'light' : 'dark';
      localStorage.setItem('theme', newTheme);
      return newTheme;
    },
  },
});

// Preferences slice
const getInitialLanguage = () => {
  return localStorage.getItem('language') || 'en';
};

interface PreferencesState {
  language: string;
  audioCapture: boolean;
  screenCapture: boolean;
}

const initialPreferences: PreferencesState = {
  language: getInitialLanguage(),
  audioCapture: true,
  screenCapture: true,
};

const preferencesSlice = createSlice({
  name: 'preferences',
  initialState: initialPreferences,
  reducers: {
    setLanguage: (state, action: PayloadAction<string>) => {
      state.language = action.payload;
      localStorage.setItem('language', action.payload);
    },
    setAudioCapture: (state, action: PayloadAction<boolean>) => {
      state.audioCapture = action.payload;
    },
    setScreenCapture: (state, action: PayloadAction<boolean>) => {
      state.screenCapture = action.payload;
    },
  },
});

// Conversations slice
const getInitialSidebarState = (): SidebarState => {
  const stored = localStorage.getItem('sidebar');
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      return {
        isCollapsed: parsed.isCollapsed ?? false,
        width: parsed.width ?? 280,
        isVisible: parsed.isVisible ?? true,
      };
    } catch {
      // Fallback to defaults if parsing fails
    }
  }
  return {
    isCollapsed: false,
    width: 280,
    isVisible: true,
  };
};

const initialConversationsState: ConversationsState = {
  items: {},
  ids: [],
  pinnedIds: [],
  searchResults: [],
  isLoading: false,
  error: null,
  activeConversationId: null,
  searchTerm: '',
  groupBy: 'date',
};

const conversationsSlice = createSlice({
  name: 'conversations',
  initialState: initialConversationsState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setConversations: (state, action: PayloadAction<Conversation[]>) => {
      state.items = {};
      state.ids = [];
      action.payload.forEach(conversation => {
        state.items[conversation._id] = conversation;
        state.ids.push(conversation._id);
        if (conversation.isPinned) {
          state.pinnedIds.push(conversation._id);
        }
      });
    },
    addConversation: (state, action: PayloadAction<Conversation>) => {
      const conversation = action.payload;
      state.items[conversation._id] = conversation;
      if (!state.ids.includes(conversation._id)) {
        state.ids.unshift(conversation._id);
      }
      if (conversation.isPinned && !state.pinnedIds.includes(conversation._id)) {
        state.pinnedIds.push(conversation._id);
      }
    },
    updateConversation: (state, action: PayloadAction<Conversation>) => {
      const conversation = action.payload;
      state.items[conversation._id] = conversation;
      
      // Update pinned status
      if (conversation.isPinned && !state.pinnedIds.includes(conversation._id)) {
        state.pinnedIds.push(conversation._id);
      } else if (!conversation.isPinned) {
        state.pinnedIds = state.pinnedIds.filter(id => id !== conversation._id);
      }
    },
    removeConversation: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      delete state.items[id];
      state.ids = state.ids.filter(conversationId => conversationId !== id);
      state.pinnedIds = state.pinnedIds.filter(pinnedId => pinnedId !== id);
      state.searchResults = state.searchResults.filter(resultId => resultId !== id);
      
      if (state.activeConversationId === id) {
        state.activeConversationId = state.ids[0] || null;
      }
    },
    setActiveConversation: (state, action: PayloadAction<string | null>) => {
      state.activeConversationId = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setSearchResults: (state, action: PayloadAction<string[]>) => {
      state.searchResults = action.payload;
    },
    setGroupBy: (state, action: PayloadAction<'date' | 'type' | 'tag' | 'pinned'>) => {
      state.groupBy = action.payload;
    },
    clearSearch: (state) => {
      state.searchTerm = '';
      state.searchResults = [];
    },
  },
});

// Sidebar slice
const sidebarSlice = createSlice({
  name: 'sidebar',
  initialState: getInitialSidebarState(),
  reducers: {
    setCollapsed: (state, action: PayloadAction<boolean>) => {
      state.isCollapsed = action.payload;
      localStorage.setItem('sidebar', JSON.stringify(state));
    },
    setWidth: (state, action: PayloadAction<number>) => {
      state.width = action.payload;
      localStorage.setItem('sidebar', JSON.stringify(state));
    },
    setVisible: (state, action: PayloadAction<boolean>) => {
      state.isVisible = action.payload;
      localStorage.setItem('sidebar', JSON.stringify(state));
    },
    toggleCollapsed: (state) => {
      state.isCollapsed = !state.isCollapsed;
      localStorage.setItem('sidebar', JSON.stringify(state));
    },
  },
});

export const store = configureStore({
  reducer: {
    theme: themeSlice.reducer,
    preferences: preferencesSlice.reducer,
    conversations: conversationsSlice.reducer,
    sidebar: sidebarSlice.reducer,
    propertyAnalysis: propertyAnalysisReducer,
    trading: tradingReducer,
  },
});

export const { setTheme, toggleTheme } = themeSlice.actions;
export const { setLanguage, setAudioCapture, setScreenCapture } = preferencesSlice.actions;
export const {
  setLoading,
  setError,
  setConversations,
  addConversation,
  updateConversation,
  removeConversation,
  setActiveConversation,
  setSearchTerm,
  setSearchResults,
  setGroupBy,
  clearSearch,
} = conversationsSlice.actions;
export const {
  setCollapsed,
  setWidth,
  setVisible,
  toggleCollapsed,
} = sidebarSlice.actions;

// Property Analysis exports
export {
  analyzeProperty,
  analyzeAllTools,
  initializeAnalysis,
  updateAnalysisProgress,
  cancelAnalysis,
  clearActiveAnalysis,
  addToHistory,
  removeFromHistory,
  clearHistory,
  updateExportSettings,
  setProgressModalOpen,
  setResultsTabOpen,
  setExportModalOpen,
  resetUIState,
  selectPropertyAnalysis,
  selectActiveAnalysis,
  selectAnalysisHistory,
  selectAnalysisProgress,
  selectAnalysisResults,
  selectExportSettings,
  selectUIState,
  selectIsAnalysisActive,
  selectAnalysisById,
} from './store/propertyAnalysisSlice';

// Trading Analysis exports
export {
  analyzeTradingResponse,
  generatePineScriptFromLevels,
  initializeTradingAnalysis,
  updateTradingProgress,
  cancelTradingAnalysis,
  clearActiveTradingAnalysis,
  addToTradingHistory,
  removeFromTradingHistory,
  clearTradingHistory,
  updateTradingExportSettings,
  setPineScriptModalOpen,
  setTradingProgressModalOpen,
  setTradingExportModalOpen,
  resetTradingUIState,
  setCurrentTradingLevels,
  selectTradingAnalysis,
  selectActiveTradingAnalysis,
  selectTradingHistory,
  selectTradingProgress,
  selectTradingResults,
  selectTradingExportSettings,
  selectTradingUIState,
  selectIsTradingAnalysisActive,
  selectTradingAnalysisById,
  selectCurrentTradingLevels,
  selectCurrentPineScript,
} from './store/tradingSlice';

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector; 